import React, { useMemo } from 'react';
import { Avatar, type AvatarProps } from 'antd';
import { resolveMediaUrl } from '@/core/supabase/mediaUrl.js';
import {
  getDiceBearAvatarUri,
  resolveAvatarSeed,
  type SupportedDiceBearStyle,
} from '@/shared/utils/dicebear.js';

export interface UserAvatarUser {
  id?: string | null;
  phone?: string | null;
  email?: string | null;
  avatarUrl?: string | null;
  avatar_url?: string | null;
  fullName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}

export interface UserAvatarProps extends Omit<AvatarProps, 'src'> {
  src?: string | null;
  seed?: string | null;
  user?: UserAvatarUser | null;
  diceBearStyle?: SupportedDiceBearStyle;
}

/**
 * Reusable user avatar component.
 * - Displays custom uploaded photo if present (via resolveMediaUrl).
 * - Falls back to deterministic DiceBear avatar (default style: Cameo) using seed order: userId ?? phone ?? email.
 */
export const UserAvatar: React.FC<UserAvatarProps> = ({
  src,
  seed,
  user,
  diceBearStyle = 'glyphs',
  size = 32,
  style,
  children,
  ...rest
}) => {
  const customAvatarUrl = src ?? user?.avatarUrl ?? user?.avatar_url;

  const avatarSrc = useMemo(() => {
    if (customAvatarUrl && customAvatarUrl.trim()) {
      return resolveMediaUrl(customAvatarUrl.trim());
    }

    const resolvedSeed =
      seed ||
      resolveAvatarSeed({
        userId: user?.id,
        phone: user?.phone,
        email: user?.email,
      });

    const numericSize = typeof size === 'number' ? size : 40;
    return getDiceBearAvatarUri(resolvedSeed, {
      style: diceBearStyle,
      size: numericSize,
    });
  }, [customAvatarUrl, seed, user, diceBearStyle, size]);

  return (
    <Avatar size={size} src={avatarSrc} style={style} {...rest}>
      {children}
    </Avatar>
  );
};
